import { Alert, Card, Loading, signedIn, tokenFromHash } from '@avrahami/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { sessionApi } from '../api/session';
import { Centered } from '../components/Layout';
import { he } from '../i18n/he';
import { homePath } from '../lib/nav';
import { sessionChanged } from '../lib/session-sync';
import { goToStore } from '../lib/store-path';

/**
 * Where the platform admin sends a member of the platform staff who manages a store (adr/0031):
 * the entry token in the address is exchanged, once, for this admin's own session of that store.
 */
export function EnterPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Read once, then taken out of the address: the token must not stay in the history.
  const [token] = useState(() => tokenFromHash(window.location.hash));
  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  // A query, so the one-time token is sent once however often the page is mounted.
  const entry = useQuery({
    queryKey: ['store-entry', token],
    queryFn: () => sessionApi.enterStore(token ?? ''),
    enabled: token !== undefined,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const me = entry.data;
  // The store's session is its own (adr/0033): the tab opens at the store's address.
  useEffect(() => {
    if (me === undefined) return;
    const store = me.activeTenant;
    sessionChanged(queryClient, signedIn(me), store === null ? undefined : [store.tenantId]);
    if (store === null) {
      void navigate({ to: '/no-store', replace: true });
      return;
    }
    goToStore(store.tenantId, homePath(store.capabilities), true);
  }, [me, queryClient, navigate]);

  if (token !== undefined && !entry.isError) {
    return (
      <Centered>
        <Loading />
      </Centered>
    );
  }
  return (
    <Centered>
      <Card title={he.enter.title}>
        <div className="flex flex-col gap-4" data-testid="enter-failed">
          <Alert>{he.enter.expired}</Alert>
          <Link to="/login" search={{}} className="text-sm font-medium text-brand-700 underline">
            {he.enter.toLogin}
          </Link>
        </div>
      </Card>
    </Centered>
  );
}
